import React, { useState, useEffect, useRef } from 'react';
import { Card, Button } from '../ui';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  getConversationById,
  subscribeToMessages,
  sendMessage,
  markAsRead,
  archiveConversation,
  unarchiveConversation
} from '../../services/messagingService';
import { Conversation, Message } from '../../types';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';

interface ConversationViewProps {
  conversationId: string;
  onBack?: () => void;
  isArchived?: boolean;
}

const channelBadge = (channel: string) => {
  switch (channel) {
    case 'sms':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-50 text-green-700">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          SMS
        </span>
      );
    case 'email':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-orange-50 text-orange-700">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Email
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp
        </span>
      );
  }
};

const ConversationView: React.FC<ConversationViewProps> = ({
  conversationId,
  onBack,
  isArchived = false
}) => {
  const { selectedOrganization } = useApp();
  const { userData: _userData } = useAuth();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!conversationId) return;

    setLoading(true);
    setError(null);

    getConversationById(conversationId)
      .then(setConversation)
      .catch((err) => {
        console.error('Error loading conversation:', err);
        setError('Failed to load conversation');
      });

    const unsubscribe = subscribeToMessages(conversationId, (newMessages) => {
      setMessages(newMessages);
      setLoading(false);
    });

    if (!isArchived) {
      markAsRead(conversationId).catch(console.error);
    }

    return () => {
      unsubscribe();
    };
  }, [conversationId, isArchived]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (text: string) => {
    if (!selectedOrganization?.id || !text.trim() || !conversation) return;

    setSending(true);
    setError(null);

    const result = await sendMessage(
      conversationId,
      text.trim(),
      selectedOrganization.id,
      conversation.channel
    );

    if (!result.success) {
      setError(result.error || 'Failed to send message');
    }

    setSending(false);
  };

  const handleArchive = async () => {
    try {
      if (isArchived) {
        await unarchiveConversation(conversationId);
      } else {
        await archiveConversation(conversationId);
      }
      onBack?.();
    } catch (err) {
      setError('Failed to update conversation');
    }
  };

  const channel = conversation?.channel || 'whatsapp';
  const isSessionExpired = channel === 'whatsapp' && conversation && !conversation.sessionActive;
  const contactInfo = channel === 'email'
    ? conversation?.participantEmail
    : conversation?.participantPhone;

  if (loading) {
    return (
      <Card className="h-full flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </Card>
    );
  }

  if (!conversation) {
    return (
      <Card className="h-full flex items-center justify-center min-h-[400px]">
        <p className="text-secondary-500">Conversation not found</p>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col min-h-[500px]">
      {/* Header */}
      <div className="p-4 border-b border-secondary-200 flex items-center gap-4">
        {onBack && (
          <button
            onClick={onBack}
            className="lg:hidden p-2 hover:bg-secondary-100 rounded-lg"
          >
            <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Avatar */}
        <div className={`
          w-10 h-10 rounded-full flex items-center justify-center text-lg font-semibold
          ${conversation.participantType === 'player'
            ? 'bg-blue-100 text-blue-700'
            : conversation.participantType === 'guardian'
            ? 'bg-purple-100 text-purple-700'
            : 'bg-gray-100 text-gray-700'
          }
        `}>
          {conversation.participantName.charAt(0).toUpperCase()}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-secondary-900 truncate">
              {conversation.participantType === 'unknown'
                ? (contactInfo || 'Unknown')
                : conversation.participantName}
            </h3>
            {channelBadge(channel)}
          </div>
          <div className="flex items-center gap-2">
            <span className={`
              px-2 py-0.5 text-xs rounded-full
              ${conversation.participantType === 'player'
                ? 'bg-blue-50 text-blue-700'
                : conversation.participantType === 'guardian'
                ? 'bg-purple-50 text-purple-700'
                : 'bg-gray-50 text-gray-700'
              }
            `}>
              {conversation.participantType === 'player'
                ? 'Player'
                : conversation.participantType === 'guardian'
                ? 'Guardian'
                : 'Unknown Contact'}
            </span>
            {conversation.participantType !== 'unknown' && contactInfo && (
              <span className="text-sm text-secondary-500">
                {contactInfo}
              </span>
            )}
          </div>
          {channel === 'email' && conversation.subject && (
            <p className="text-sm text-secondary-600 truncate mt-1">
              Subject: {conversation.subject}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleArchive}
          >
            {isArchived ? 'Unarchive' : 'Archive'}
          </Button>
        </div>
      </div>

      {/* Session Warning (WhatsApp only) */}
      {isSessionExpired && !isArchived && (
        <div className="px-4 py-3 bg-warning-50 border-b border-warning-200">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-warning-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-warning-800">
                24-hour messaging window expired
              </p>
              <p className="text-sm text-warning-700 mt-1">
                The recipient must reply before you can send another message. WhatsApp requires a recent interaction to prevent spam.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-secondary-500">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOutbound={message.direction === 'outbound'}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-error-50 border-t border-error-200">
          <p className="text-sm text-error-700">{error}</p>
        </div>
      )}

      {/* Input */}
      {!isArchived && (
        <MessageInput
          onSend={handleSend}
          disabled={sending || !!isSessionExpired}
          placeholder={isSessionExpired ? 'Waiting for recipient to reply...' : 'Type a message...'}
          channel={channel}
        />
      )}
    </Card>
  );
};

export default ConversationView;
